import { useGetSalesChannels } from '@agos/api-client/orders'
import { channelActive, useAuth, useFranchiseModules } from '@agos/shared'

import type { NavGuard } from '../generated/nav-tree'
import type { RouteGuard } from '../module-contract'

export interface AccessContext {
  hasPermission: (permission: string) => boolean
  isModuleEnabled: (key: string) => boolean
  /** Whether a sales channel is switched on — §9.281. Fails open while unknown. */
  isChannelActive: (slug: string) => boolean
  systemRole: string | undefined
  isSuperAdmin: boolean
}

/**
 * Whether the signed-in employee may see something — one evaluator for the
 * sidebar and the router both.
 *
 * Legacy has two, and they disagree in seventeen places: an `admin` holding
 * `settings_general` is offered *Všeobecné nastavenia* in the sidebar and then
 * served the 404 page, because the route checks `systemRole === "main_admin"`
 * instead (FRONTEND-BUGS.md FB-001, FB-002, FB-003). Sharing the evaluator does
 * not fix that on its own — the two gates still carry different *data* — but it
 * means the fix, once the owner has chosen which side wins, is made in one
 * place and cannot drift again.
 *
 * An empty `franchiseModule` is legacy's way of switching the tier gate off for
 * a base feature while keeping the permission gate. It is a value, not an
 * absence, and is honoured as such.
 */
export function canAccess(
  guard: NavGuard | RouteGuard | undefined | null,
  access: AccessContext,
): boolean {
  return accessRefusal(guard, access) === null
}

/**
 * Why the gate said no, or `null` if it said yes.
 *
 * The sidebar only needs the boolean — an item it may not show, it does not show. A **route** needs the
 * reason, because somebody is looking at the screen and legacy's answer was the 404 page: a page that
 * exists, that they are simply not allowed to open, reported as one that does not exist. Every refusal here
 * names which of the four gates closed and what it wanted.
 */
export interface AccessRefusal {
  gate: 'super-admin' | 'role' | 'permission' | 'franchise-module' | 'sales-channel'
  /** The roles admitted, the flag wanted, or the module key — whatever the gate was checking. */
  wanted: readonly string[]
}

export function accessRefusal(
  guard: NavGuard | RouteGuard | undefined | null,
  access: AccessContext,
): AccessRefusal | null {
  if (!guard) return null

  if ('superAdminOnly' in guard && guard.superAdminOnly && !access.isSuperAdmin) {
    return { gate: 'super-admin', wanted: [] }
  }

  // §9.117 — an outright grant, checked before every narrowing gate below. A driver holds no
  // permission flags, so the driver app has to admit them by role or not at all.
  if ('orSystemRoles' in guard && guard.orSystemRoles && guard.orSystemRoles.length > 0) {
    const granted = guard.orSystemRoles as readonly string[]
    if (access.systemRole && granted.includes(access.systemRole)) return null
  }

  if ('systemRoles' in guard && guard.systemRoles && guard.systemRoles.length > 0) {
    const admitted = guard.systemRoles as readonly string[]
    if (!access.systemRole || !admitted.includes(access.systemRole)) {
      return { gate: 'role', wanted: admitted }
    }
  }

  if ('permission' in guard && guard.permission && !access.hasPermission(guard.permission)) {
    return { gate: 'permission', wanted: [guard.permission] }
  }

  if ('anyPermission' in guard && guard.anyPermission) {
    if (!guard.anyPermission.some((permission) => access.hasPermission(permission))) {
      return { gate: 'permission', wanted: guard.anyPermission }
    }
  }

  if ('franchiseModule' in guard && guard.franchiseModule) {
    if (!access.isModuleEnabled(guard.franchiseModule)) {
      return { gate: 'franchise-module', wanted: [guard.franchiseModule] }
    }
  }

  // §9.281 — a page that exists for one sales channel goes when that channel does.
  if ('salesChannel' in guard && guard.salesChannel) {
    if (!access.isChannelActive(guard.salesChannel)) {
      return { gate: 'sales-channel', wanted: [guard.salesChannel] }
    }
  }

  return null
}

/** The signed-in employee's gates, assembled once for the sidebar and the router both. */
export function useAccessContext(): AccessContext {
  const { hasPermission, user } = useAuth()
  const { isModuleEnabled } = useFranchiseModules()
  // §9.281 — one request, read by the evaluator both the sidebar and the router use. `isActive` is
  // the only field wanted, and an answer that has not arrived fails open.
  const { data: channelData } = useGetSalesChannels()
  const isChannelActive = (slug: string): boolean =>
    channelActive(Array.isArray(channelData) ? channelData : undefined, slug)
  return {
    hasPermission,
    isModuleEnabled,
    isChannelActive,
    systemRole: user?.systemRole,
    isSuperAdmin: Boolean(user?.isSuperAdmin),
  }
}
